// Employee-mode control strip. Rendered at the top of the config panel only
// when a rep is authenticated. Gives the three superpowers:
//
//   • Promo ID  — server resolves it to a template + discount in one shot
//   • Template  — override the HubSpot quote template (active templates only)
//   • Discount  — percent or fixed-dollar off, applied as a quote line item
//
// All of it rides along on the generate call and is re-validated server-side;
// the preview/totals the customer sees don't change here (the discount lands
// on the real HubSpot quote), so we show the rep a live estimate inline.

import { useEffect, useState } from "react";

import { api, ApiError } from "../api";
import type { DiscountType, EmployeePromo, QuoteTemplate } from "../api";
import { useConfigurator } from "../store";
import { formatDollars } from "../format";
import { ManagePromosModal } from "./ManagePromosModal";

export function EmployeePanel() {
  const { state, selectedSubtotalCents, setEmployee, lockEmployee } =
    useConfigurator();
  const emp = state.employee;

  const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
  const [tplError, setTplError] = useState<string | null>(null);
  const [tplLoading, setTplLoading] = useState(false);
  const [promos, setPromos] = useState<EmployeePromo[]>([]);
  const [manageOpen, setManageOpen] = useState(false);

  // Reload the rep-facing promo dropdown (e.g. after an admin edits promos).
  function reloadPromos() {
    if (!emp.token) return;
    api
      .employeePromos(emp.token)
      .then(setPromos)
      .catch(() => {
        /* keep the existing list on a transient failure */
      });
  }

  // Load the template + promo lists once unlocked. Best-effort: if either
  // fails the rep can still build the quote; that input just falls back to a
  // sensible default (default template / no promo).
  useEffect(() => {
    if (!emp.active || !emp.token) return;
    const token = emp.token;
    let cancelled = false;
    setTplLoading(true);
    setTplError(null);
    api
      .employeeTemplates(token)
      .then((t) => {
        if (!cancelled) setTemplates(t);
      })
      .catch((err) => {
        if (!cancelled)
          setTplError(
            err instanceof ApiError ? err.message : "Couldn't load templates.",
          );
      })
      .finally(() => {
        if (!cancelled) setTplLoading(false);
      });
    api
      .employeePromos(token)
      .then((p) => {
        if (!cancelled) setPromos(p);
      })
      .catch(() => {
        /* no promos / unreachable — the picker just stays empty */
      });
    return () => {
      cancelled = true;
    };
  }, [emp.active, emp.token]);

  // Live estimate for the rep's manual (global) discount — off the whole
  // subtotal. The real number is computed server-side at generate time.
  const estDiscountCents =
    emp.discountType === "percent"
      ? Math.round((selectedSubtotalCents * (emp.discountValue || 0)) / 100)
      : emp.discountType === "fixed"
        ? Math.min(Math.round((emp.discountValue || 0) * 100), selectedSubtotalCents)
        : 0;

  return (
    <section className="mb-2 border border-amber/60 bg-amber/10">
      <div className="flex items-center justify-between border-b border-amber/40 bg-amber/20 px-4 py-2">
        <span className="font-display text-[13px] font-extrabold uppercase tracking-tag text-graphite">
          ⚡ Employee mode — {emp.repName ?? "Gammill"}
        </span>
        <button
          type="button"
          onClick={lockEmployee}
          className="font-sans text-[13px] font-semibold text-graphite underline-offset-2 hover:underline"
        >
          Exit
        </button>
      </div>

      <div className="flex flex-col gap-4 px-4 py-4">
        {/* Promo — sets template + discount server-side. Dropdown of the
            promos configured in the admin (free of typos). */}
        <label className="block">
          <span className="mb-1 block font-sans text-[13px] font-semibold text-ink">
            Promo
          </span>
          <select
            value={emp.promoId}
            onChange={(e) => setEmployee({ promoId: e.target.value })}
            className="block w-full border border-line bg-white px-3 py-2 font-sans text-[15px] text-ink focus:border-ink focus:outline-none"
          >
            <option value="">No promo</option>
            {promos.map((p) => (
              <option key={p.code} value={p.code}>
                {p.label}
              </option>
            ))}
          </select>
          <span className="mt-1 block font-sans text-[13px] text-steel">
            {promos.length
              ? "Applies its template + discount automatically. Overrides below."
              : emp.isAdmin
                ? "No promos yet — add one with “Manage promos”."
                : "No promos configured yet."}
          </span>
          {emp.isAdmin && (
            <button
              type="button"
              onClick={() => setManageOpen(true)}
              className="mt-1 font-sans text-[13px] font-semibold text-graphite underline underline-offset-2"
            >
              Manage promos
            </button>
          )}
        </label>

        {/* Template override. */}
        <label className="block">
          <span className="mb-1 block font-sans text-[13px] font-semibold text-ink">
            Quote template
          </span>
          <select
            value={emp.templateId}
            onChange={(e) => setEmployee({ templateId: e.target.value })}
            disabled={tplLoading}
            className="block w-full border border-line bg-white px-3 py-2 font-sans text-[15px] text-ink focus:border-ink focus:outline-none disabled:opacity-60"
          >
            <option value="">
              {tplLoading ? "Loading templates…" : "Default template"}
            </option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          {tplError && (
            <span className="mt-1 block font-sans text-[13px] text-red-700">
              {tplError}
            </span>
          )}
        </label>

        {/* Manual discount — the global "discount" line, e.g. a trade-in.
            Separate from (and additive to) a promo's discount. */}
        <div>
          <span className="mb-1 block font-sans text-[13px] font-semibold text-ink">
            Discount / Trade-in
          </span>
          <div className="flex items-center gap-2">
            <select
              value={emp.discountType}
              onChange={(e) =>
                setEmployee({ discountType: e.target.value as "" | DiscountType })
              }
              className="border border-line bg-white px-3 py-2 font-sans text-[15px] text-ink focus:border-ink focus:outline-none"
            >
              <option value="">None</option>
              <option value="percent">% off</option>
              <option value="fixed">$ off</option>
            </select>
            {emp.discountType && (
              <div className="flex items-center gap-1">
                {emp.discountType === "fixed" && (
                  <span className="font-sans text-[15px] text-ink">$</span>
                )}
                <input
                  type="number"
                  min={0}
                  step="any"
                  value={emp.discountValue || ""}
                  onChange={(e) =>
                    setEmployee({ discountValue: Number(e.target.value || 0) })
                  }
                  className="w-28 border border-line bg-white px-3 py-2 font-sans text-[15px] text-ink focus:border-ink focus:outline-none"
                />
                {emp.discountType === "percent" && (
                  <span className="font-sans text-[15px] text-ink">%</span>
                )}
              </div>
            )}
          </div>
          {emp.discountType && (
            <input
              type="text"
              value={emp.discountLabel}
              onChange={(e) => setEmployee({ discountLabel: e.target.value })}
              placeholder="Label shown on the quote (e.g. Trade-in)"
              className="mt-2 block w-full border border-line bg-white px-3 py-2 font-sans text-[15px] text-ink focus:border-ink focus:outline-none"
            />
          )}
          {estDiscountCents > 0 && (
            <span className="mt-1 block font-sans text-[13px] text-graphite">
              ≈ −{formatDollars(estDiscountCents)} off the current subtotal
              {emp.discountLabel.trim() ? ` (${emp.discountLabel.trim()})` : ""}.
            </span>
          )}
        </div>
      </div>

      {emp.isAdmin && (
        <ManagePromosModal
          open={manageOpen}
          onClose={() => {
            setManageOpen(false);
            reloadPromos();
          }}
        />
      )}
    </section>
  );
}
